import React, { useState, useEffect, useRef } from 'react';
import {
  ArrowLeft,
  Save,
  Plus,
  Trash2,
  GripVertical,
  ChevronDown,
  ChevronUp,
  Image as ImageIcon,
  Type,
  Video,
  Table,
  Quote,
  AtSign,
  Loader,
  X,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { ImageUpload } from '../../components/cms/ImageUpload';
import RichTextEditor from '../../components/cms/RichTextEditor';
import blogApiService from '../../services/blogApiService';
import type {
  Blog,
  BlogAuthor,
  BlogSection,
  BlogBlock,
  BlogBlockType,
  BlogCreateDTO,
  BlogCategory,
} from '../../types/blog';
import { blogPostSchema } from '../../types/blog';
import { generateSlug } from '../../utils/helpers';
import { extractErrorMessage } from '../../utils/errorHandler';
import logger from '../../utils/logger';
import toast from 'react-hot-toast';

interface BlogPostEditorProps {
  post: Blog | null;
  onClose: (saved?: boolean) => void;
}

/**
 * Normalizes category data from API/state to consistent format
 * Handles both string and object formats for backward compatibility
 */
function normalizeCategoryTags(categories: unknown): BlogCategory[] {
  if (!Array.isArray(categories)) return [];

  let tempCategoryId = -1;

  return categories
    .map((category): BlogCategory | null => {
      // If already a proper object with id
      if (category && typeof category === 'object' && 'id' in category) {
        const categoryId = (category as { id?: number }).id;
        return { 
          id: typeof categoryId === 'number' ? categoryId : tempCategoryId--,
          name: (category as { name?: string }).name
        };
      }
      
      // If it's a string, keep it as a temporary frontend tag until resolved with backend category list
      if (typeof category === 'string' && category.trim()) {
        return { 
          id: tempCategoryId--,
          name: category.trim() 
        };
      }
      
      // If it has a name property
      if (category && typeof category === 'object' && 'name' in category) {
        const name = (category as { name?: unknown }).name;
        if (typeof name === 'string' && name.trim()) {
          return { 
            id: tempCategoryId--,
            name: name.trim() 
          };
        }
      }
      
      return null;
    })
    .filter((cat): cat is BlogCategory => cat !== null);
}

function reconcileSelectedCategoriesWithAvailable(
  selected: BlogCategory[],
  available: BlogCategory[]
): BlogCategory[] {
  const normalize = (value: string) => value.trim().toLowerCase();

  return selected.map((category) => {
    if (typeof category.id === 'number' && category.id > 0) {
      return category;
    }

    const name = category.name?.trim();
    if (!name) {
      return category;
    }

    const matched = available.find(
      (candidate) => normalize(candidate.name ?? '') === normalize(name)
    );

    return matched ? { id: matched.id, name: matched.name ?? name } : category;
  });
}

// ==================== TABLE EDITOR ====================

// Serialised table format stored in block.content
interface TableSerialised {
  headers: string[];
  rows: string[][];
}

// Internal editor state — rows and columns carry stable IDs so React keys are stable
interface TableColumn {
  id: string;
  label: string;
}

interface TableRow {
  id: string;
  cells: string[];
}

interface TableEditorState {
  columns: TableColumn[];
  rows: TableRow[];
}

let _rowIdSeq = 0;
const genRowId = () => `row_${(++_rowIdSeq).toString(36)}`;
const genColId = () => `col_${(++_rowIdSeq).toString(36)}`;

const EMPTY_TABLE_HEADERS = ['Column 1', 'Column 2', 'Column 3'];

function parseTableContent(raw: string): TableEditorState {
  try {
    if (raw) {
      const p = JSON.parse(raw) as TableSerialised;
      if (Array.isArray(p.headers) && Array.isArray(p.rows)) {
        return {
          columns: p.headers.map((label) => ({ id: genColId(), label })),
          rows: p.rows.map((cells) => ({ id: genRowId(), cells: [...cells] })),
        };
      }
    }
  } catch { /* fall through to default */ }
  return {
    columns: EMPTY_TABLE_HEADERS.map((label) => ({ id: genColId(), label })),
    rows: [
      { id: genRowId(), cells: ['', '', ''] },
      { id: genRowId(), cells: ['', '', ''] },
    ],
  };
}

function serialiseTable(state: TableEditorState): string {
  const out: TableSerialised = {
    headers: state.columns.map((c) => c.label),
    rows: state.rows.map((r) => r.cells),
  };
  return JSON.stringify(out);
}

const TableEditorBlock: React.FC<{ value: string; onChange: (v: string) => void }> = ({
  value,
  onChange,
}) => {
  const [table, setTable] = useState<TableEditorState>(() => parseTableContent(value));
  const lastCommittedRef = useRef(value);

  useEffect(() => {
    if (value !== lastCommittedRef.current) {
      lastCommittedRef.current = value;
      setTable(parseTableContent(value));
    }
  }, [value]);

  const commit = (next: TableEditorState) => {
    const serialised = serialiseTable(next);
    lastCommittedRef.current = serialised;
    setTable(next);
    onChange(serialised);
  };

  const updateHeader = (colId: string, val: string) =>
    commit({
      ...table,
      columns: table.columns.map((col) => (col.id === colId ? { ...col, label: val } : col)),
    });

  const updateCell = (rowId: string, colIdx: number, val: string) =>
    commit({
      ...table,
      rows: table.rows.map((row) =>
        row.id === rowId ? { ...row, cells: row.cells.map((c, i) => (i === colIdx ? val : c)) } : row
      ),
    });

  const addRow = () =>
    commit({
      ...table,
      rows: [...table.rows, { id: genRowId(), cells: new Array<string>(table.columns.length).fill('') }],
    });

  const removeRow = (rowId: string) => {
    if (table.rows.length <= 1) return;
    commit({ ...table, rows: table.rows.filter((r) => r.id !== rowId) });
  };

  const addColumn = () =>
    commit({
      columns: [...table.columns, { id: genColId(), label: `Column ${table.columns.length + 1}` }],
      rows: table.rows.map((row) => ({ ...row, cells: [...row.cells, ''] })),
    });

  const removeColumn = (colId: string) => {
    if (table.columns.length <= 1) return;
    const ci = table.columns.findIndex((c) => c.id === colId);
    if (ci === -1) return;
    commit({
      columns: table.columns.filter((c) => c.id !== colId),
      rows: table.rows.map((row) => ({ ...row, cells: row.cells.filter((_, i) => i !== ci) })),
    });
  };

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full min-w-max text-sm border-collapse">
          <thead>
            <tr className="bg-slate-50 dark:bg-slate-800/60">
              {table.columns.map((col, colIdx) => (
                <th
                  key={col.id}
                  className="border-r last:border-r-0 border-slate-200 dark:border-slate-700 p-1.5 text-left"
                >
                  <div className="group/hcell flex items-center gap-1">
                    <input
                      value={col.label}
                      onChange={(e) => updateHeader(col.id, e.target.value)}
                      className="w-full min-w-[80px] bg-transparent font-semibold text-slate-700 dark:text-slate-200 text-xs focus:outline-none focus:bg-white dark:focus:bg-slate-800 rounded px-1 py-0.5"
                      placeholder={`Column ${colIdx + 1}`}
                    />
                    {table.columns.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeColumn(col.id)}
                        className="shrink-0 text-slate-300 hover:text-red-400 opacity-0 group-hover/hcell:opacity-100 transition-opacity"
                        aria-label="Remove column"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </th>
              ))}
              <th className="p-1.5 w-8">
                <button
                  type="button"
                  onClick={addColumn}
                  className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                  aria-label="Add column"
                >
                  <Plus className="h-3.5 w-3.5" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row) => (
              <tr
                key={row.id}
                className="border-t border-slate-200 dark:border-slate-700 group/row hover:bg-slate-50/50 dark:hover:bg-slate-800/20"
              >
                {table.columns.map((col, colIdx) => (
                  <td
                    key={col.id}
                    className="border-r last:border-r-0 border-slate-200 dark:border-slate-700 p-1.5"
                  >
                    <input
                      value={row.cells[colIdx] ?? ''}
                      onChange={(e) => updateCell(row.id, colIdx, e.target.value)}
                      className="w-full min-w-[80px] bg-transparent text-slate-700 dark:text-slate-300 text-xs focus:outline-none focus:bg-white dark:focus:bg-slate-800 rounded px-1 py-0.5"
                      placeholder="—"
                    />
                  </td>
                ))}
                <td className="p-1.5 w-8">
                  {table.rows.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRow(row.id)}
                      className="text-slate-300 hover:text-red-400 opacity-0 group-hover/row:opacity-100 transition-opacity"
                      aria-label="Remove row"
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        onClick={addRow}
        className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors px-1"
      >
        <Plus className="h-3 w-3" />
        Add Row
      </button>
    </div>
  );
};

const BLOCK_TYPES: { type: BlogBlockType; label: string; icon: React.ElementType }[] = [
  { type: 'PARAGRAPH', label: 'Text', icon: Type },
  { type: 'IMAGE', label: 'Image', icon: ImageIcon },
  { type: 'VIDEO', label: 'Video', icon: Video },
  { type: 'TABLE', label: 'Table', icon: Table },
  { type: 'QUOTE', label: 'Quote', icon: Quote },
  { type: 'BUTTON', label: 'Button', icon: AtSign },
];

function getPublishToggleBg(status: string, published: boolean) {
  if (status === 'ARCHIVED') return 'bg-slate-200 dark:bg-slate-700 cursor-not-allowed';
  if (published) return 'bg-emerald-500';
  return 'bg-slate-300 dark:bg-slate-600';
}

function getSaveButtonContent(isSaving: boolean, isCoverUploading: boolean, isEditing: boolean) {
  if (isSaving) {
    return (
      <>
        <Loader className="h-4 w-4 mr-1.5 animate-spin" />
        Saving...
      </>
    );
  }

  if (isCoverUploading) {
    return (
      <>
        <Loader className="h-4 w-4 mr-1.5 animate-spin" />
        Uploading image...
      </>
    );
  }

  return (
    <>
      <Save className="h-4 w-4 mr-1.5" />
      {isEditing ? 'Update' : 'Create'} Post
    </>
  );
}

interface FooterVisibilityCardProps {
  activeFooter: boolean;
  isEditing: boolean;
  isFooterToggling: boolean;
  isSaving: boolean;
  onToggle: () => void;
  status?: string;
}

const FooterVisibilityCard: React.FC<FooterVisibilityCardProps> = ({
  activeFooter,
  isEditing,
  isFooterToggling,
  isSaving,
  onToggle,
  status,
}) => {
  const isDisabled = isSaving || isFooterToggling || status === 'ARCHIVED';
  
  const footerRowBg = isDisabled
    ? 'bg-slate-100 dark:bg-slate-800 opacity-60'
    : 'bg-slate-50 dark:bg-slate-800/50';

  const footerToggleBg = (() => {
    if (isDisabled) return 'bg-slate-200 dark:bg-slate-700 cursor-not-allowed';
    if (activeFooter) return 'bg-emerald-500';
    return 'bg-slate-300 dark:bg-slate-600';
  })();

  return (
    <div className={`flex items-center justify-between py-2 px-3 rounded-lg ${footerRowBg}`}>
      <div className="flex flex-col min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm text-slate-600 dark:text-slate-300">Show in Footer</span>
        </div>
      </div>
      <button
        type="button"
        disabled={isDisabled}
        aria-label={activeFooter ? 'Disable footer visibility' : 'Enable footer visibility'}
        onClick={onToggle}
        className={`relative w-10 h-5 shrink-0 rounded-full transition-colors ${footerToggleBg}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
            activeFooter ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
};

const ButtonBlockEditor: React.FC<{
  block: BlogBlock;
  onChange: (updates: Partial<BlogBlock>) => void;
}> = ({ block, onChange }) => {
  return (
    <div className="space-y-3">
      <Input
        value={block.titleText ?? ''}
        onChange={(e) => onChange({ titleText: e.target.value })}
        placeholder="Button text (e.g., Book Now)"
        className="h-8 text-sm"
      />
      <Input
        value={block.mediaUrl ?? ''}
        onChange={(e) => onChange({ mediaUrl: e.target.value })}
        placeholder="Button URL (https://...)"
        className="h-9"
      />
      <Input
        value={block.content}
        onChange={(e) => onChange({ content: e.target.value })}
        placeholder="Optional supporting text"
        className="h-8 text-sm"
      />
      <Input
        value={block.altText ?? ''}
        onChange={(e) => onChange({ altText: e.target.value })}
        placeholder="Optional style tag (primary / secondary)"
        className="h-8 text-sm"
      />
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-3 py-2">
        <p className="text-[11px] text-slate-500 dark:text-slate-400">
          Uses existing block fields only: <span className="font-medium">titleText</span> as label and <span className="font-medium">mediaUrl</span> as link.
        </p>
      </div>
    </div>
  );
};

const BlogPostEditor: React.FC<BlogPostEditorProps> = ({ post, onClose }) => {
  const isEditing = !!post;

  // Form state
  const [title, setTitle] = useState(post?.title ?? '');
  const [slug, setSlug] = useState(post?.slug ?? '');
  const [excerpt, setExcerpt] = useState(post?.excerpt ?? '');
  const [coverImage, setCoverImage] = useState(post?.coverImage ?? '');
  const [authorId, setAuthorId] = useState<number>(post?.author?.id ?? 0);
  const [status, setStatus] = useState<string>(
    (post?.status?.toUpperCase() as string) ?? 'DRAFT'
  );
  const published = status === 'PUBLISHED';
  const [activeFooter, setActiveFooter] = useState(Boolean(post?.activeFooter));
  const [categories, setCategories] = useState<BlogCategory[]>(
    normalizeCategoryTags(post?.categories)
  );
  const [metaTitle, setMetaTitle] = useState(post?.metaTitle ?? '');
  const [metaDescription, setMetaDescription] = useState(post?.metaDescription ?? '');
  const [sections, setSections] = useState<BlogSection[]>(
    post?.sections?.length
      ? post.sections.map((s) => ({ ...s, blocks: [...s.blocks] }))
      : [{ title: '', sectionOrder: 0, blocks: [{ type: 'PARAGRAPH' as BlogBlockType, content: '', blockOrder: 0 }] }]
  );
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(Boolean(post?.slug));

  // UI state
  const [authors, setAuthors] = useState<BlogAuthor[]>([]);
  const [availableCategories, setAvailableCategories] = useState<BlogCategory[]>([]);
  const [categoryToAddId, setCategoryToAddId] = useState<string>('');
  const [newCategoryName, setNewCategoryName] = useState('');
  const [isCategoryCreating, setIsCategoryCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isCoverUploading, setIsCoverUploading] = useState(false);
  const [isFooterToggling, setIsFooterToggling] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set([0]));

  const normalizeCategoryName = (name: string) => name.trim().toLowerCase();
  const isPersistedCategoryId = (id: number | undefined) => typeof id === 'number' && id > 0;

  useEffect(() => {
    blogApiService
      .getAuthors()
      .then(setAuthors)
      .catch((err) => logger.error('Failed to load authors:', err));

    blogApiService
      .getBlogCategories()
      .then((fetchedCategories) => {
        setAvailableCategories(fetchedCategories);
        setCategories((prev) => reconcileSelectedCategoriesWithAvailable(prev, fetchedCategories));
      })
      .catch((err) => logger.error('Failed to load blog categories:', err));
  }, []);

  const addSelectedCategory = (category: BlogCategory) => {
    setCategories((prev) => {
      const alreadySelected = prev.some((selected) => selected.id === category.id);
      if (alreadySelected) {
        return prev;
      }

      return [...prev, { id: category.id, name: category.name }];
    });
  };

  const removeSelectedCategory = (categoryId: number) => {
    setCategories((prev) => prev.filter((category) => category.id !== categoryId));
  };

  const handleAddCategoryFromDropdown = (value: string) => {
    const parsedId = Number(value);
    if (!Number.isFinite(parsedId) || parsedId <= 0) {
      return;
    }

    const selected = availableCategories.find((category) => category.id === parsedId);
    if (!selected) {
      return;
    }

    addSelectedCategory(selected);
    setCategoryToAddId('');
  };

  const handleCreateCategory = async () => {
    const trimmedName = newCategoryName.trim();
    if (!trimmedName) {
      return;
    }

    const existing = availableCategories.find(
      (category) =>
        normalizeCategoryName(category.name ?? '') === normalizeCategoryName(trimmedName)
    );

    if (existing) {
      addSelectedCategory(existing);
      setNewCategoryName('');
      return;
    }

    try {
      setIsCategoryCreating(true);
      const created = await blogApiService.createBlogCategory(trimmedName);
      setAvailableCategories((prev) => [...prev, created]);
      addSelectedCategory(created);
      setNewCategoryName('');
      toast.success('Category created');
    } catch (error) {
      toast.error(extractErrorMessage(error));
      logger.error('Failed to create blog category:', error);
    } finally {
      setIsCategoryCreating(false);
    }
  };

  const resolveCategoriesForSave = async (): Promise<BlogCategory[]> => {
    const availableByName = new Map(
      availableCategories
        .filter((cat) => typeof cat.name === 'string' && cat.name.trim())
        .map((cat) => [normalizeCategoryName(cat.name as string), cat])
    );

    const resolved: BlogCategory[] = [];
    const seenIds = new Set<number>();
    const seenNames = new Set<string>();
    const createdCategories: BlogCategory[] = [];

    for (const category of categories) {
      const rawName = category.name ?? '';
      const name = rawName.trim();
      if (!name) {
        continue;
      }

      const normalizedName = normalizeCategoryName(name);
      if (seenNames.has(normalizedName)) {
        continue;
      }
      seenNames.add(normalizedName);

      if (isPersistedCategoryId(category.id) && !seenIds.has(category.id)) {
        seenIds.add(category.id);
        resolved.push({ id: category.id, name });
        continue;
      }

      const available = availableByName.get(normalizedName);
      if (available && !seenIds.has(available.id)) {
        seenIds.add(available.id);
        resolved.push({ id: available.id, name: available.name ?? name });
        continue;
      }

      // Preserve compatibility for older post data where category names might exist without IDs.
      const created = await blogApiService.createBlogCategory(name);
      if (isPersistedCategoryId(created.id) && !seenIds.has(created.id)) {
        seenIds.add(created.id);
        resolved.push({ id: created.id, name: created.name ?? name });
        createdCategories.push({ id: created.id, name: created.name ?? name });
        availableByName.set(normalizedName, created);
      }
    }

    if (createdCategories.length > 0) {
      setAvailableCategories((prev) => {
        const merged = [...prev];
        const existingIds = new Set(prev.map((cat) => cat.id));
        for (const created of createdCategories) {
          if (!existingIds.has(created.id)) {
            merged.push(created);
            existingIds.add(created.id);
          }
        }
        return merged;
      });
    }

    return resolved;
  };

  // Auto-generate slug from title for new posts, or when slug is still empty.
  useEffect(() => {
    if (!slugManuallyEdited && title && (!isEditing || !slug.trim())) {
      setSlug(generateSlug(title));
    }
  }, [title, slugManuallyEdited, isEditing, slug]);

  const toggleSection = (index: number) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  // Section management
  const addSection = () => {
    setSections((prev) => {
      const newIndex = prev.length;
      setExpandedSections((ePrev) => new Set([...ePrev, newIndex]));
      return [
        ...prev,
        {
          title: '',
          sectionOrder: newIndex,
          blocks: [{ type: 'PARAGRAPH' as BlogBlockType, content: '', blockOrder: 0 }],
        },
      ];
    });
  };

  const removeSection = (index: number) => {
    setSections((prev) => prev.filter((_, i) => i !== index).map((s, i) => ({ ...s, sectionOrder: i })));
  };

  const updateSectionTitle = (index: number, value: string) => {
    setSections((prev) => prev.map((s, i) => (i === index ? { ...s, title: value } : s)));
  };

  const moveSection = (index: number, direction: 'up' | 'down') => {
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= sections.length) return;
    setSections((prev) => {
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((s, i) => ({ ...s, sectionOrder: i }));
    });
  };

  // Block management
  const addBlock = (sectionIndex: number, type: BlogBlockType) => {
    const initialContent =
      type === 'TABLE'
        ? serialiseTable({
            columns: EMPTY_TABLE_HEADERS.map((label) => ({ id: genColId(), label })),
            rows: [
              { id: genRowId(), cells: ['', '', ''] },
              { id: genRowId(), cells: ['', '', ''] },
            ],
          })
        : '';
    setSections((prev) =>
      prev.map((s, i) =>
        i === sectionIndex
          ? {
              ...s,
              blocks: [
                ...s.blocks,
                {
                  type,
                  content: initialContent,
                  mediaUrl: '',
                  altText: '',
                  titleText: '',
                  blockOrder: s.blocks.length,
                  _tmpKey: `tmp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
                },
              ],
            }
          : s
      )
    );
  };

  const reorderBlocks = (blocks: BlogBlock[]) =>
    blocks.map((b, bi) => ({ ...b, blockOrder: bi }));

  const updateSectionBlocks = (
    sectionIndex: number,
    updater: (blocks: BlogBlock[]) => BlogBlock[]
  ) => {
    setSections((prev) =>
      prev.map((s, i) => (i === sectionIndex ? { ...s, blocks: updater(s.blocks) } : s))
    );
  };

  const removeBlock = (sectionIndex: number, blockIndex: number) => {
    updateSectionBlocks(sectionIndex, (blocks) =>
      reorderBlocks(blocks.filter((_, bi) => bi !== blockIndex))
    );
  };

  const updateBlock = (sectionIndex: number, blockIndex: number, updates: Partial<BlogBlock>) => {
    updateSectionBlocks(sectionIndex, (blocks) =>
      blocks.map((b, bi) => (bi === blockIndex ? { ...b, ...updates } : b))
    );
  };

  const moveBlock = (sectionIndex: number, blockIndex: number, direction: 'up' | 'down') => {
    const targetIndex = direction === 'up' ? blockIndex - 1 : blockIndex + 1;
    updateSectionBlocks(sectionIndex, (blocks) => {
      if (targetIndex < 0 || targetIndex >= blocks.length) return blocks;
      const newBlocks = [...blocks];
      [newBlocks[blockIndex], newBlocks[targetIndex]] = [newBlocks[targetIndex], newBlocks[blockIndex]];
      return reorderBlocks(newBlocks);
    });
  };

  // Save
  const handleSave = async () => {
    if (isCoverUploading) {
      toast.error('Please wait for cover image upload to finish');
      return;
    }

    setErrors({});

    let categoriesToSend: BlogCategory[] = [];

    try {
      categoriesToSend = await resolveCategoriesForSave();
      setCategories(categoriesToSend);
    } catch (error) {
      toast.error(extractErrorMessage(error));
      logger.error('Failed to resolve blog categories:', error);
      return;
    }

    const validation = blogPostSchema.safeParse({
      title,
      slug,
      excerpt,
      coverImage,
      authorId,
      status,
      categories: categoriesToSend,
      metaTitle,
      metaDescription,
      published,
    });

    if (!validation.success) {
      const fieldErrors: Record<string, string> = {};
      validation.error.errors.forEach((e) => {
        fieldErrors[e.path[0] as string] = e.message;
      });
      setErrors(fieldErrors);
      toast.error('Please fix the validation errors');
      return;
    }

    const selectedAuthor = authors.find((a) => a.id === authorId);
    if (!selectedAuthor) {
      setErrors({ authorId: 'Selected author not found. Please refresh and try again.' });
      toast.error('Please select a valid author');
      return;
    }

    // Clean sections — strip internal flags
    const cleanSections: BlogSection[] = sections.map((s, si) => ({
      ...(s.id ? { id: s.id } : {}),
      title: s.title,
      sectionOrder: si,
      blocks: s.blocks.map((b, bi) => ({
        ...(b.id ? { id: b.id } : {}),
        type: b.type,
        content: b.content,
        mediaUrl: b.mediaUrl ?? '',
        altText: b.altText ?? '',
        titleText: b.titleText ?? '',
        blockOrder: bi,
      })),
    }));

    const dto: BlogCreateDTO = {
      title,
      slug,
      excerpt,
      coverImage: coverImage.trim(),
      author: selectedAuthor,
      status,
      activeFooter,
      categories: categoriesToSend,
      metaTitle,
      metaDescription,
      published,
      sections: cleanSections,
    };

    try {
      setIsSaving(true);
      if (isEditing) {
        await blogApiService.updateBlog(post.id, dto);
        toast.success('Post updated successfully');
      } else {
        await blogApiService.createBlog(dto);
        toast.success('Post created successfully');
      }
      onClose(true);
    } catch (error) {
      toast.error(extractErrorMessage(error));
      logger.error('Failed to save blog post:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleFooterToggle = async () => {
    if (!isEditing || !post?.id) {
      setActiveFooter((current) => !current);
      return;
    }

    try {
      setIsFooterToggling(true);
      const desiredFooterState = !activeFooter;
      const updatedPost = await blogApiService.toggleBlogFooterActive(post.id, desiredFooterState);
      const nextFooterState =
        typeof updatedPost.activeFooter === 'boolean'
          ? updatedPost.activeFooter
          : desiredFooterState;
      setActiveFooter(nextFooterState);
      toast.success(nextFooterState ? 'Footer active for this post' : 'Footer hidden for this post');
    } catch (error) {
      toast.error(extractErrorMessage(error));
      logger.error('Failed to toggle blog footer:', error);
    } finally {
      setIsFooterToggling(false);
    }
  };

  // Block renderer
  const renderBlock = (block: BlogBlock, sectionIndex: number, blockIndex: number, totalBlocks: number) => {
    const blockTypeInfo = BLOCK_TYPES.find((bt) => bt.type === block.type);
    const Icon = blockTypeInfo?.icon ?? Type;
    let blockKey = `idx-${sectionIndex}-${blockIndex}`;
    if (block.id !== undefined) {
      blockKey = `block-${block.id}`;
    } else if (block._tmpKey) {
      blockKey = block._tmpKey;
    }

    return (
      <div
        key={blockKey}
        className="group relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-4 space-y-3"
      >
        {/* Block header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className="h-4 w-4 text-slate-400" />
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">
              {blockTypeInfo?.label}
            </span>
          </div>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={() => moveBlock(sectionIndex, blockIndex, 'up')}
              disabled={blockIndex === 0}
              className="p-1 rounded text-slate-400 hover:text-slate-600 disabled:opacity-30"
            >
              <ChevronUp className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => moveBlock(sectionIndex, blockIndex, 'down')}
              disabled={blockIndex === totalBlocks - 1}
              className="p-1 rounded text-slate-400 hover:text-slate-600 disabled:opacity-30"
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => removeBlock(sectionIndex, blockIndex)}
              className="p-1 rounded text-slate-400 hover:text-red-500"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Block content based on type */}
        {block.type === 'PARAGRAPH' && (
          <RichTextEditor
            value={block.content}
            onChange={(val) => updateBlock(sectionIndex, blockIndex, { content: val })}
            placeholder="Write your content..."
            minHeight="160px"
          />
        )}

        {block.type === 'IMAGE' && (
          <div className="space-y-3">
            <ImageUpload
              value={block.mediaUrl}
              onChange={(url) => updateBlock(sectionIndex, blockIndex, { mediaUrl: url })}
              label="Block Image"
            />
            <Input
              value={block.altText ?? ''}
              onChange={(e) => updateBlock(sectionIndex, blockIndex, { altText: e.target.value })}
              placeholder="Alt text for accessibility"
              className="h-8 text-sm"
            />
            <Input
              value={block.content}
              onChange={(e) => updateBlock(sectionIndex, blockIndex, { content: e.target.value })}
              placeholder="Caption (optional)"
              className="h-8 text-sm"
            />
          </div>
        )}

        {block.type === 'VIDEO' && (
          <div className="space-y-3">
            <Input
              value={block.mediaUrl ?? ''}
              onChange={(e) => updateBlock(sectionIndex, blockIndex, { mediaUrl: e.target.value })}
              placeholder="Video URL (YouTube, Vimeo, etc.)"
              className="h-9"
            />
            <Input
              value={block.titleText ?? ''}
              onChange={(e) => updateBlock(sectionIndex, blockIndex, { titleText: e.target.value })}
              placeholder="Video title"
              className="h-8 text-sm"
            />
            <Input
              value={block.content}
              onChange={(e) => updateBlock(sectionIndex, blockIndex, { content: e.target.value })}
              placeholder="Description (optional)"
              className="h-8 text-sm"
            />
          </div>
        )}

        {block.type === 'TABLE' && (
          <div className="space-y-3">
            <Input
              value={block.titleText ?? ''}
              onChange={(e) => updateBlock(sectionIndex, blockIndex, { titleText: e.target.value })}
              placeholder="Table caption (optional)"
              className="h-8 text-sm"
            />
            <TableEditorBlock
              value={block.content}
              onChange={(v) => updateBlock(sectionIndex, blockIndex, { content: v })}
            />
          </div>
        )}

        {block.type === 'QUOTE' && (
          <div className="space-y-3">
            <div className="relative">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-400 rounded-full" />
              <textarea
                value={block.content}
                onChange={(e) => updateBlock(sectionIndex, blockIndex, { content: e.target.value })}
                placeholder="Enter the quote..."
                className="w-full min-h-[80px] pl-5 pr-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-amber-50/50 dark:bg-amber-950/20 text-slate-800 dark:text-slate-200 italic text-sm resize-y focus:outline-none focus:ring-2 focus:ring-amber-300"
              />
            </div>
            <Input
              value={block.titleText ?? ''}
              onChange={(e) => updateBlock(sectionIndex, blockIndex, { titleText: e.target.value })}
              placeholder="Attribution (e.g., — Author Name)"
              className="h-8 text-sm"
            />
          </div>
        )}

        {block.type === 'BUTTON' && (
          <ButtonBlockEditor
            block={block}
            onChange={(updates) => updateBlock(sectionIndex, blockIndex, updates)}
          />
        )}
      </div>
    );
  };

  const publishToggleBg = getPublishToggleBg(status, published);

  return (
    <div className="p-6 space-y-6">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => onClose()}
          className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Posts
        </button>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onClose()}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isSaving || isCoverUploading}
            className="bg-gradient-to-r from-slate-900 to-slate-700 dark:from-slate-100 dark:to-slate-300 text-white dark:text-slate-900"
          >
            {getSaveButtonContent(isSaving, isCoverUploading, isEditing)}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* LEFT: Main editor */}
        <div className="xl:col-span-2 space-y-6">
          {/* Title & Slug */}
          <Card className="border-slate-200 dark:border-slate-800">
            <CardContent className="p-5 space-y-4">
              <div>
                <label htmlFor="post-title" className="block text-xs font-medium text-slate-500 mb-1.5">Title</label>
                <Input
                  id="post-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter post title"
                  className={`text-lg font-semibold ${errors.title ? 'border-red-400' : ''}`}
                />
                {errors.title && <p className="text-xs text-red-500 mt-1">{errors.title}</p>}
              </div>
              <div>
                <label htmlFor="post-slug" className="block text-xs font-medium text-slate-500 mb-1.5">Slug</label>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-400">/blog/</span>
                  <Input
                    id="post-slug"
                    value={slug}
                    onChange={(e) => {
                      setSlugManuallyEdited(true);
                      setSlug(e.target.value);
                    }}
                    placeholder="post-url-slug"
                    className={`flex-1 h-8 text-slate-300 text-sm font-mono ${errors.slug ? 'border-red-400' : ''}`}
                  />
                </div>
                <p className="mt-1 text-[11px] text-slate-400">Editable URL slug. Keep it short, lowercase, and hyphen-separated.</p>
                {errors.slug && <p className="text-xs text-red-500 mt-1">{errors.slug}</p>}
              </div>
              <div>
                <label htmlFor="post-excerpt" className="block text-xs font-medium text-slate-500 mb-1.5">Description</label>
                <textarea
                  id="post-excerpt"
                  value={excerpt}
                  onChange={(e) => setExcerpt(e.target.value)}
                  placeholder="Brief summary of the post..."
                  className="w-full min-h-[80px] p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-slate-400"
                  maxLength={500}
                />
                <p className="text-xs text-slate-400 text-right mt-0.5">{excerpt.length}/500</p>
              </div>
            </CardContent>
          </Card>

          {/* Content Sections */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold text-slate-700 dark:text-slate-300 tracking-wider uppercase">
                Content Sections
              </h2>
              <Button variant="outline" size="sm" onClick={addSection} className="h-7 text-xs">
                <Plus className="h-3 w-3 mr-1" />
                Add Section
              </Button>
            </div>

            {sections.map((section, sectionIndex) => (
              <Card
                key={section.id ?? `new-section-${section.sectionOrder}`}
                className="border-slate-200 dark:border-slate-800 overflow-hidden"
              >
                {/* Section header */}
                <div
                  className="flex items-center gap-3 px-4 py-3 bg-slate-50/80 dark:bg-slate-800/50"
                >
                  <GripVertical className="h-4 w-4 text-slate-300 flex-shrink-0" />
                  <Input
                    value={section.title}
                    onChange={(e) => updateSectionTitle(sectionIndex, e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    placeholder={`Section ${sectionIndex + 1} title`}
                    className="flex-1 h-7 text-sm font-medium bg-transparent border-0 shadow-none focus-visible:ring-1 px-1"
                  />
                  <div className="flex items-center gap-0.5">
                    <span className="text-[10px] text-slate-400 mr-2">
                      {section.blocks.length === 1 ? '1 block' : `${section.blocks.length} blocks`}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); moveSection(sectionIndex, 'up'); }}
                      disabled={sectionIndex === 0}
                      className="p-1 rounded text-slate-400 hover:text-slate-600 disabled:opacity-30"
                    >
                      <ChevronUp className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); moveSection(sectionIndex, 'down'); }}
                      disabled={sectionIndex === sections.length - 1}
                      className="p-1 rounded text-slate-400 hover:text-slate-600 disabled:opacity-30"
                    >
                      <ChevronDown className="h-3.5 w-3.5" />
                    </button>
                    {sections.length > 1 && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeSection(sectionIndex); }}
                        className="p-1 rounded text-slate-400 hover:text-red-500"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {expandedSections.has(sectionIndex) ? (
                      <button
                        type="button"
                        onClick={() => toggleSection(sectionIndex)}
                        className="p-1 rounded text-slate-400 hover:text-slate-600"
                        aria-label={`Collapse section ${sectionIndex + 1}`}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => toggleSection(sectionIndex)}
                        className="p-1 rounded text-slate-400 hover:text-slate-600"
                        aria-label={`Expand section ${sectionIndex + 1}`}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Section body */}
                {expandedSections.has(sectionIndex) && (
                  <CardContent className="p-4 space-y-4">
                    {section.blocks.map((block, blockIndex) =>
                      renderBlock(block, sectionIndex, blockIndex, section.blocks.length)
                    )}

                    {/* Add block buttons */}
                    <div className="flex items-center gap-2 pt-2">
                      <span className="text-xs text-slate-400">Add block:</span>
                      {BLOCK_TYPES.map(({ type, label, icon: BIcon }) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => addBlock(sectionIndex, type)}
                          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium
                            border border-dashed border-slate-300 dark:border-slate-600
                            text-slate-500 hover:text-slate-700 hover:border-slate-400
                            dark:text-slate-400 dark:hover:text-slate-200 dark:hover:border-slate-500
                            transition-colors"
                        >
                          <BIcon className="h-3 w-3" />
                          {label}
                        </button>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        </div>

        {/* RIGHT: Sidebar */}
        <div className="space-y-5 xl:sticky xl:top-6 xl:self-start">
          {/* Publish settings */}
          <Card className="border-slate-200 dark:border-slate-800">
            <CardContent className="p-5 space-y-4">
              <h3 className="text-xs font-bold text-slate-500 tracking-wider uppercase">Publish</h3>

              <div>
                <label htmlFor="post-status" className="block text-xs font-medium text-slate-500 mb-1.5">Status</label>
                <select
                  id="post-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value.toUpperCase())}
                  className="w-full h-9 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm px-3 focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  <option value="DRAFT">Draft</option>
                  <option value="PUBLISHED">Published</option>
                  <option value="ARCHIVED">Archived</option>
                </select>
              </div>

              <div className={`flex items-center justify-between py-2 px-3 rounded-lg ${
                status === 'ARCHIVED'
                  ? 'bg-slate-100 dark:bg-slate-800 opacity-60'
                  : 'bg-slate-50 dark:bg-slate-800/50'
              }`}>
                <div className="flex flex-col">
                  <span className="text-sm text-slate-600 dark:text-slate-300">Publish</span>
                  {status === 'ARCHIVED' && (
                    <span className="text-[10px] text-slate-400 mt-0.5">Unavailable while archived</span>
                  )}
                </div>
                <button
                  type="button"
                  disabled={status === 'ARCHIVED'}
                  aria-label={published ? 'Set to draft' : 'Set to published'}
                  onClick={() => setStatus((prev) => prev === 'PUBLISHED' ? 'DRAFT' : 'PUBLISHED')}
                  className={`relative w-10 h-5 rounded-full transition-colors ${publishToggleBg}`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
                      published ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              <FooterVisibilityCard
                activeFooter={activeFooter}
                isEditing={isEditing}
                isFooterToggling={isFooterToggling}
                isSaving={isSaving}
                onToggle={handleFooterToggle}
                status={status}
              />

              {isEditing && (
                <div className="text-xs text-slate-400 space-y-1">
                  <p>Created: {post.createdAt ? new Date(post.createdAt).toLocaleDateString() : '—'}</p>
                  <p>Updated: {post.updatedAt ? new Date(post.updatedAt).toLocaleDateString() : '—'}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Author */}
          <Card className="border-slate-200 dark:border-slate-800">
            <CardContent className="p-5 space-y-3">
              <label htmlFor="author-select" className="text-xs font-bold text-slate-500 tracking-wider uppercase">Author</label>
              <select
                id="author-select"
                value={authorId}
                onChange={(e) => setAuthorId(Number(e.target.value))}
                className={`w-full h-9 rounded-lg border ${
                  errors.authorId ? 'border-red-400' : 'border-slate-200 dark:border-slate-700'
                } bg-white dark:bg-slate-900 text-sm px-3 focus:outline-none focus:ring-2 focus:ring-slate-400`}
              >
                <option value={0}>Select an author</option>
                {authors.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
              {errors.authorId && <p className="text-xs text-red-500">{errors.authorId}</p>}
            </CardContent>
          </Card>

          {/* Cover Image */}
          <Card className="border-slate-200 dark:border-slate-800">
            <CardContent className="p-5 space-y-3">
              <h3 className="text-xs font-bold text-slate-500 tracking-wider uppercase">Cover Image</h3>
              <ImageUpload
                value={coverImage}
                onChange={setCoverImage}
                onRemove={() => setCoverImage('')}
                onUploadingChange={setIsCoverUploading}
                label="Upload cover"
                description="Recommended: 1200x630px"
              />
            </CardContent>
          </Card>

          {/* Categories */}
          <Card className="border-slate-200 dark:border-slate-800">
            <CardContent className="p-5 space-y-3">
              <h3 className="text-xs font-bold text-slate-500 tracking-wider uppercase">Categories</h3>
              <div className="flex items-center gap-2">
                <select
                  value={categoryToAddId}
                  onChange={(e) => {
                    const selectedValue = e.target.value;
                    setCategoryToAddId(selectedValue);
                    handleAddCategoryFromDropdown(selectedValue);
                  }}
                  className="flex-1 h-9 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm px-3 focus:outline-none focus:ring-2 focus:ring-slate-400"
                >
                  <option value="">Select category</option>
                  {availableCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name ?? `Category ${category.id}`}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <Input
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="Create new category"
                  className="h-9"
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      void handleCreateCategory();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void handleCreateCategory()}
                  disabled={!newCategoryName.trim() || isCategoryCreating}
                >
                  {isCategoryCreating ? 'Adding...' : 'Create'}
                </Button>
              </div>

              {categories.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {categories.map((category) => (
                    <span
                      key={category.id}
                      className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2.5 py-1 text-xs text-slate-700 dark:text-slate-200"
                    >
                      {category.name ?? `Category ${category.id}`}
                      <button
                        type="button"
                        className="text-slate-500 hover:text-red-500"
                        aria-label={`Remove ${category.name ?? 'category'}`}
                        onClick={() => removeSelectedCategory(category.id)}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* SEO */}
          <Card className="border-slate-200 dark:border-slate-800">
            <CardContent className="p-5 space-y-4">
              <h3 className="text-xs font-bold text-slate-500 tracking-wider uppercase">SEO</h3>
              <div>
                <label htmlFor="meta-title" className="block text-xs font-medium text-slate-500 mb-1.5">Meta Title</label>
                <Input
                  id="meta-title"
                  value={metaTitle}
                  onChange={(e) => setMetaTitle(e.target.value)}
                  placeholder="SEO title"
                  className="h-8 text-sm"
                  maxLength={100}
                />
                <p className="text-xs text-slate-400 text-right mt-0.5">{metaTitle.length}/100</p>
              </div>
              <div>
                <label htmlFor="meta-description" className="block text-xs font-medium text-slate-500 mb-1.5">Meta Description</label>
                <textarea
                  id="meta-description"
                  value={metaDescription}
                  onChange={(e) => setMetaDescription(e.target.value)}
                  placeholder="SEO description"
                  className="w-full min-h-[60px] p-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-slate-400"
                  maxLength={300}
                />
                <p className="text-xs text-slate-400 text-right mt-0.5">{metaDescription.length}/300</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BlogPostEditor;
